import { MsalProvider, useMsal } from '@azure/msal-react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { PropsWithChildren, ReactElement, Suspense } from 'react';

import { Toaster } from '@/components/ui/toast';

import { FullscreenLoading } from './components/layout/fullscreen-loading';
import { GlobalErrorDialog } from './components/shared/global-error-dialog';
import { pca } from './config/msal';
import { AuthProvider } from './contexts/auth-context';
import { LocaleProvider } from './contexts/locale-context';
import { queryClient } from './lib/query-client';
import { router } from './router';

// Mode A: MSAL demo shell (active in main.tsx / production)
export function AppShellForDemo({ children }: PropsWithChildren): ReactElement {
	return (
		<MsalProvider instance={pca}>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider attribute="class" defaultTheme="system" enableSystem>
					<LocaleProvider>
						{children}
						{/* <ReactQueryDevtools /> */}
					</LocaleProvider>
				</ThemeProvider>
			</QueryClientProvider>
		</MsalProvider>
	);
}

// Mode B: Real auth shell (used in main.local.tsx)
export function AppShellForRealAuth({ children }: PropsWithChildren): ReactElement {
	return (
		<Suspense fallback={<FullscreenLoading />}>
			<ThemeProvider attribute="class" defaultTheme="system" enableSystem>
				<QueryClientProvider client={queryClient}>
					<LocaleProvider>
						<AuthProvider>{children}</AuthProvider>
					</LocaleProvider>
				</QueryClientProvider>
			</ThemeProvider>
		</Suspense>
	);
}

// App component for Mode A — requires MsalProvider in the tree
export function App(): ReactElement {
	const qc = useQueryClient();
	const msal = useMsal();

	return (
		<>
			<RouterProvider router={router} context={{ queryClient: qc, msal }} />
			<GlobalErrorDialog />
			<Toaster />
		</>
	);
}

// App component for Mode B — no MSAL dependency
export function AppForRealAuth(): ReactElement {
	const qc = useQueryClient();

	return (
		<>
			<RouterProvider router={router} context={{ queryClient: qc }} />
			<GlobalErrorDialog />
			<Toaster />
		</>
	);
}
